import { useInViewport, useSize } from 'ahooks'
import cn from 'classnames'
import { get } from 'lodash'
import type { ReactNode } from 'react'
import React, { useEffect, useContext, useRef } from 'react'

import { LayoutScroll } from 'components/LayoutPage'
import Empty from 'components/uiKit/Empty'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { t } from 'services/Translation'
import { extendTestData, testProps } from 'utils/test/qaData'

import Checkbox from '../Checkbox'
import Loader from '../Loader'
import ColumnTitle from './ColumnTitle'
import HeaderCheckbox from './HeaderCheckbox'
import Resizable from './Resizable'
import * as s from './Table.module.scss'
import { TableContext } from './TableContext'
import { MIN_COLUMN_WIDTH } from './constants'
import { TablePagination, useActualTotal } from './index'
import type { ColumnType, ITablesExtendsData, TableProps } from './types'
import { getColumnIdentity } from './utils'

interface ITableCellProps {
  name?: string
  sticky?: boolean
  renderCell: () => ReactNode
  value?: string
  align?: ColumnType<unknown>['align']
  width?: string | number
  index: number
  itemType?: 'folder' | 'project' | 'file'
}

const TableCell = ({
  sticky = false,
  renderCell,
  value,
  align,
  index,
  name,
  itemType,
}: ITableCellProps) => {
  const alignStyle = align ? s[align] : s.left

  return (
    <div
      className={cn({ [s.sticky]: sticky }, s.cell, alignStyle)}
      style={{ overflow: 'hidden' }}
      {...testProps({ el: 'tableCell', name, label: value, index: index, itemType })}
    >
      {renderCell()}
    </div>
  )
}

const defaultRender: ColumnType<unknown>['render'] = (value) => {
  return value ? String(value) : ''
}

const TableCore = <T extends ITablesExtendsData>({
  checked,
  columns,
  dataSource,
  footer,
  total = dataSource.length,
  onClickRow,
  rowClassName,
  className,
  pagination,
  loading,
  onChangeSelection,
  name,
  selectedItems,
  getCheckboxProps,
  disableRow,
  empty,
  onColumnResize,
  resizeableColumns = true,
  withBourders = false,
}: TableProps<T>) => {
  const { onLoadMore, hasNextPage } = useContext(TableContext)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const size = useSize(tableContainerRef)
  const filteredColumns = columns.filter((col) => !col.hidden)

  const ref = useRef<HTMLTableRowElement | null>(null)
  const measurementRefs = useRef<(HTMLDivElement | null)[]>([])

  const actualTotal = useActualTotal(total, !loading)

  const [inViewport] = useInViewport(ref)

  useEffect(() => {
    if (inViewport && onLoadMore && hasNextPage) {
      onLoadMore()
    }
  }, [inViewport, onLoadMore, hasNextPage])

  useEffect(() => {
    onChangeSelection?.(EMPTY_ARRAY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource])

  useEffect(() => {
    return () => {
      onChangeSelection?.([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickRowHandler = (record: T, event: React.MouseEvent) =>
    onClickRow && onClickRow(event, record, 'view')

  const handleCheck = (index: number) => (value: boolean) => {
    if (onChangeSelection && selectedItems) {
      onChangeSelection(
        value
          ? [...selectedItems, dataSource[index]]
          : selectedItems.filter(({ id }) => id !== dataSource[index].id),
      )
    }
  }

  const showTable = dataSource.length > 0 && !loading
  const showEmpty = !dataSource.length && !loading
  const checkboxWidth = checked ? 'max-content ' : ''
  const gridTemplate =
    checkboxWidth +
    filteredColumns
      .map((c) =>
        c.width ? `${Math.max(c.width, MIN_COLUMN_WIDTH)}px` : `minmax(${MIN_COLUMN_WIDTH}px, 1fr)`,
      )
      .join(' ')

  const tableHeader = columns
    .map((col, colIndex) => {
      if (col.hidden) {
        measurementRefs.current[colIndex] = null
        return
      }

      return (
        <div
          className={cn(s.headerCell, {
            [s.sticky]: colIndex === 0,
            [s.noBordersHeader]: col.title === undefined,
          })}
          key={getColumnIdentity(col)}
          ref={(el) => {
            if (!el) {
              return
            }
            measurementRefs.current[colIndex] = el
          }}
        >
          <ColumnTitle config={col} name={name} />
          {resizeableColumns && (
            <Resizable
              container={measurementRefs.current[colIndex]}
              onResize={(data) => onColumnResize?.(colIndex, data)}
              width={col.width}
            />
          )}
        </div>
      )
    })
    .filter(Boolean)

  return (
    <div className={cn(s.root, className, { [s.withBourders]: withBourders })}>
      <div style={{ height: size?.height, display: 'flex' }}>
        <LayoutScroll autoHide='leave' showBorder>
          <div className={s.tableWrapper} ref={tableContainerRef}>
            <div
              className={cn(s.header, { [s.free]: !resizeableColumns })}
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {checked && (
                <div className={cn(s.checkboxHeader, s.stickyCheckbox, s.headerCell)}>
                  <HeaderCheckbox
                    dataSource={dataSource}
                    getCheckboxProps={getCheckboxProps}
                    onChangeSelection={onChangeSelection}
                    selected={selectedItems}
                  />
                </div>
              )}
              {tableHeader}
            </div>
            <div className={s.body}>
              {showTable &&
                dataSource.map((row, index) => (
                  <div
                    className={cn(rowClassName?.(row, index), s.row, {
                      [s.disabledRow]: disableRow && disableRow(row, index),
                      [s.free]: !resizeableColumns,
                    })}
                    key={index}
                    onClick={(e) => onClickRowHandler(row, e)}
                    style={{
                      gridTemplateColumns: gridTemplate,
                    }}
                  >
                    {checked && selectedItems && (
                      <div className={s.stickyCheckbox} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          disabled={getCheckboxProps?.(row).disabled}
                          name='tableCheckbox'
                          onChange={handleCheck(index)}
                          testData={extendTestData({
                            index,
                            label:
                              getCheckboxProps?.(row)?.name ||
                              (typeof row.name === 'string' ? row.name : row.name?.props.text),
                          })}
                          value={selectedItems.some(({ id }) => id === row.id)}
                        />
                      </div>
                    )}
                    {filteredColumns.map(({ dataIndex, render: renderCell, align }, colIndex) => {
                      const __typename = (row as { __typename?: string }).__typename
                      const itemType =
                        __typename === 'ProjectGroup' || __typename === 'FileMetaGroup'
                          ? 'folder'
                          : __typename === 'Project'
                            ? 'project'
                            : __typename === 'FileMeta'
                              ? 'file'
                              : undefined
                      return (
                        <TableCell
                          align={align}
                          index={index}
                          itemType={itemType}
                          key={typeof dataIndex === 'string' ? dataIndex : dataIndex?.join('.')}
                          name={typeof dataIndex === 'string' ? dataIndex : dataIndex?.join('.')}
                          renderCell={() =>
                            renderCell?.(dataIndex ? get(row, dataIndex) : undefined, row, index) ||
                            defaultRender(dataIndex ? get(row, dataIndex) : undefined, row, index)
                          }
                          sticky={colIndex === 0}
                          value={dataIndex ? String(get(row, dataIndex)) : ''}
                        />
                      )
                    })}
                    {onLoadMore && hasNextPage && (
                      <div className={s.loaderRow} ref={ref}>
                        <div className={s.loader}>
                          <Loader name='loadMoreLoader' />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </LayoutScroll>
      </div>
      {!!footer && showTable && !loading && <div className={s.tableFooter}>{footer}</div>}
      {pagination && !loading && (
        <div className={s.wrapperPagination}>
          <TablePagination total={actualTotal} />
        </div>
      )}
      {showEmpty && !loading && (
        <div className={s.emptyWrapper}>
          <Empty text={t('common.nothingFound')} {...empty} grow />
        </div>
      )}
      {loading && <Loader name={`${name}.tableLoader`} />}
    </div>
  )
}

export default TableCore
